import { useCallback, useEffect, useRef, useState } from "react";
import { InputField } from "components";
import SC from "./style";
import theme from "theme";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import QuillToolbar, { modules, formats } from "./QuillToolbar";
import { apiPost } from "api/callApi";
import { byteToMB, compressImage, showAlert } from "utils/helper";
import TooltipText from "components/TooltipText";
import {
  formatAttachmentSize,
  getAttachmentName,
  getAttachmentPreview,
  getAttachmentSizeBytes,
  MAX_JOURNAL_ATTACHMENT_SIZE,
  uploadJournalAttachments,
} from "../../utils/attachmentUpload";

const MessageContent = (props) => {
  const { setData, data, errorMessage, isEditing } = props;
  const [files, setFiles] = useState(data?.files || []);
  const [filePreviews, setFilePreviews] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef(null);
  const [value, setValue] = useState(null);

  const MAX_TOTAL_SIZE = MAX_JOURNAL_ATTACHMENT_SIZE;

  useEffect(() => {
    if (data?.files && data?.files.length > 0) {
      const previews = data?.files.map((file) => {
        if (file.isExistingAttachment || file.type === "text/plain") {
          return null;
        }
        return getAttachmentPreview(file) || null;
      });

      setFilePreviews(previews);

      // Cleanup function to revoke object URLs to avoid memory leaks
      return () => {
        previews.forEach((preview) => {
          if (preview?.startsWith("blob:")) {
            URL.revokeObjectURL(preview);
          }
        });
      };
    } else {
      // Clear previews if no files exist
      setFilePreviews([]);
    }
  }, [data?.files]);

  // Handle changes for subject, message, and file
  const handleChange = (property, value) => {
    setData((prevData) => ({ ...prevData, [property]: value }));
  };

  const handleChangeTextEditor = useCallback(
    (newValue) => {
      setValue(newValue);
      setData((prevData) => ({
        ...prevData,
        message: newValue,
      }));
    },
    [setData],
  );

  useEffect(() => {
    setFiles(data?.files || []);
  }, [data?.files]);

  const addFiles = async (selectedFiles, fileInput) => {
    if (!selectedFiles.length) return;

    const newTotalSize =
      files.reduce((sum, file) => sum + getAttachmentSizeBytes(file), 0) +
      selectedFiles.reduce((sum, file) => sum + file.size, 0);

    if (newTotalSize > MAX_TOTAL_SIZE) {
      showAlert({
        title: "Error",
        text: `Total file size exceeds the ${byteToMB(MAX_TOTAL_SIZE)} MB limit.`,
        icon: "error",
      });
      if (fileInput) fileInput.value = "";
      return;
    }

    const validFiles = selectedFiles.filter(
      (file) => file.size <= MAX_TOTAL_SIZE,
    );

    if (validFiles.length < selectedFiles.length) {
      showAlert({
        title: "Error",
        text: `Total file size exceeds the ${byteToMB(MAX_TOTAL_SIZE)} MB limit.`,
        icon: "error",
      });
    }

    try {
      setUploadingFiles(true);
      setData((prevData) => ({ ...prevData, attachmentUploading: true }));
      const uploadedFiles = await uploadJournalAttachments(validFiles);

      setFiles((prevFiles) => [...prevFiles, ...uploadedFiles]);
      setFilePreviews((prevPreviews) => [
        ...prevPreviews,
        ...uploadedFiles.map((file) => file.previewUrl),
      ]);
      setData((prevData) => ({
        ...prevData,
        files: [...(prevData.files || []), ...uploadedFiles],
      }));
    } catch (error) {
      showAlert({
        title: "Error",
        text: error?.response?.data?.message || "Unable to upload attachment.",
        icon: "error",
      });
    } finally {
      setUploadingFiles(false);
      setData((prevData) => ({ ...prevData, attachmentUploading: false }));
      if (fileInput) fileInput.value = "";
    }
  };

  const handleFileChange = async (e) => {
    const fileInput = e.target;
    const selectedFiles = Array.from(fileInput.files);
    await addFiles(selectedFiles, fileInput);
  };

  const cleanupUploadedDraftFile = async (file) => {
    if (!file?.isUploadedAttachment || !file?.fileUrl) return;
    try {
      await apiPost("/v1/aws/file/deletefile", {
        fileKey: file.fileUrl,
      });
    } catch (error) {
      console.warn("Unable to cleanup draft attachment", error);
    }
  };

  const handleRemoveFile = async (index) => {
    const removedFile = data?.files?.[index];
    await cleanupUploadedDraftFile(removedFile);

    // Remove file and preview
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    setFilePreviews((prevPreviews) =>
      prevPreviews.filter((_, i) => i !== index),
    );

    // Update files in data
    setData((prevData) => ({
      ...prevData,
      files: prevData.files.filter((_, i) => i !== index),
      existingAttachmentIds: (prevData.existingAttachmentIds || []).filter(
        (id) => id !== removedFile?.id,
      ),
      removedAttachmentIds:
        isEditing && removedFile?.isExistingAttachment && removedFile?.id
          ? [...(prevData.removedAttachmentIds || []), removedFile.id]
          : prevData.removedAttachmentIds || [],
    }));
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    await addFiles(droppedFiles);
  };

  const handleFileDialogClick = () => {
    fileInputRef.current.click();
  };

  const quillRef = useRef(null);

  // Function to handle pasted images
  const handlePaste = async (event) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    // Check if the pasted content contains an image
    const items = (event.clipboardData || event.originalEvent.clipboardData)
      .items;
    for (const item of items) {
      if (item.type.indexOf("image") !== -1) {
        event.preventDefault(); // Prevent the default paste behavior

        const file = item.getAsFile();
        if (file) {
          // Compress the pasted image
          const compressedFile = await compressImage(file);

          // Convert the compressed image to a data URL
          const reader = new FileReader();
          reader.readAsDataURL(compressedFile);
          reader.onload = () => {
            const imageUrl = reader.result;

            // Insert the compressed image into the editor
            const range = quill.getSelection(true);
            quill.insertEmbed(range.index, "image", imageUrl, "user");
          };
        }
      }
    }
  };

  // Attach the paste event listener to the Quill editor
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      editor.root.addEventListener("paste", handlePaste);
    }

    return () => {
      if (editor) {
        editor.root.removeEventListener("paste", handlePaste);
      }
    };
  }, []);

  return (
    <>
      {/* Subject */}
      <SC.LabelContainer>
        <SC.Label>
          Subject <SC.Astrik>*</SC.Astrik>
        </SC.Label>
        <SC.Children>
          <SC.Wrapper>
            <InputField
              name="subject"
              value={data?.subject}
              onChange={handleChange}
              required
              style={{ width: "100%", height: "35px" }}
              maxLength={1000}
            />
          </SC.Wrapper>
          {errorMessage && (
            <span style={{ color: theme.colors.red, fontSize: "12px" }}>
              {errorMessage}
            </span>
          )}
        </SC.Children>
      </SC.LabelContainer>

      {/* Message */}
      <SC.LabelContainer style={{ alignItems: "flex-start" }}>
        <SC.Label>Message</SC.Label>
        <SC.Children>
          <SC.Wrapper style={{ flexDirection: "column" }}>
            <div>
              <QuillToolbar />
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={data?.message}
                onChange={handleChangeTextEditor}
                placeholder="Write something awesome..."
                modules={modules}
                formats={formats}
                style={{ height: "100%" }}
              />
            </div>
          </SC.Wrapper>
        </SC.Children>
      </SC.LabelContainer>

      {/* Attachment */}
      <SC.LabelContainer style={{ alignItems: "flex-start" }}>
        <SC.Label>Attachment</SC.Label>
        <SC.Children>
          <SC.Wrapper style={{ flexDirection: "column" }}>
            <SC.DropzoneWrapper
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={uploadingFiles ? undefined : handleFileDialogClick}
            >
              <SC.UploadFileContiner>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <SC.Span>
                    <SC.UploadIcon />
                  </SC.Span>
                  <div>
                    <span style={{ fontSize: "14px" }}>Drop your file or</span>
                    <span
                      style={{
                        color: theme.colors.blue,
                        fontSize: "14px",
                      }}
                    >
                      {" "}
                      browse
                    </span>
                  </div>
                  <span style={{ fontSize: "12px", opacity: "0.8" }}>
                    {uploadingFiles
                      ? "Uploading attachment..."
                      : "Max 25 MB file is allowed"}
                  </span>
                </div>
              </SC.UploadFileContiner>
            </SC.DropzoneWrapper>
            <input
              multiple
              type="file"
              name="file[]"
              style={{ display: "none" }}
              onChange={handleFileChange}
              ref={fileInputRef}
            />

            <SC.PreviewsContainer>
              {(data?.files || []).map((file, index) => {
                const preview = filePreviews[index];
                const fileSize = formatAttachmentSize(
                  getAttachmentSizeBytes(file),
                );

                return (
                  <SC.PreviewBox key={file?.id || file?.fileUrl || index}>
                    <SC.PreviewContainer>
                      <SC.Image src={preview || ""} alt="file preview" />
                      <SC.FileName>
                        <TooltipText
                          title={getAttachmentName(file)}
                          maxLength={60}
                        />
                        <SC.FileSize>{fileSize}</SC.FileSize>
                      </SC.FileName>
                    </SC.PreviewContainer>
                    <SC.CloseIcon onClick={() => handleRemoveFile(index)} />
                  </SC.PreviewBox>
                );
              })}
            </SC.PreviewsContainer>
          </SC.Wrapper>
        </SC.Children>
      </SC.LabelContainer>
    </>
  );
};

export default MessageContent;
